import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import StreamIcon from "@mui/icons-material/Stream";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import CustomSelect from "../../../../../components/common/Select";
import CustomTextField from "../../../../../components/common/Textfield";
import { RootState } from "../../../../../store/Index";
import { tokens } from "../../../../../theme";
import { StepComponentProps } from "../../AddBookingV2";
import StepHeader from "../StepHeader";

const ConferenceStreamingStep: React.FC<StepComponentProps> = ({
  bookingData,
  setBookingData,
  isEdit,
  onUpdate,
  submitting,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const color400 = colors.primary[400] || "#724E41";

  const handleChange = (field: string, value: unknown) => {
    setBookingData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}
    >
      <StepHeader
        title="Streaming & Branding"
        isEdit={isEdit}
        onUpdate={onUpdate}
        submitting={submitting}
      />

      {/* Streaming & Recording */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <StreamIcon sx={{ mr: 1, color: color400 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Streaming & Recording
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={!!bookingData.enable_livestreaming}
              onChange={(e) =>
                handleChange("enable_livestreaming", e.target.checked)
              }
            />
          }
          label="Enable live streaming"
        />

        {bookingData.enable_livestreaming && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 3,
              }}
            >
              <CustomSelect
                label="Streaming Platform"
                value={bookingData.streaming_platform || ""}
                onChange={(e) =>
                  handleChange("streaming_platform", e.target.value)
                }
              >
                <MenuItem value="youtube">Youtube</MenuItem>
                <MenuItem value="zoom">Zoom</MenuItem>
                <MenuItem value="teams">Microsoft Teams</MenuItem>
                <MenuItem value="webex">Cisco Webex</MenuItem>
                <MenuItem value="custom">Custom Platform</MenuItem>
                <MenuItem value="provide">We&apos;ll provide platform</MenuItem>
              </CustomSelect>

              <CustomSelect
                label="Sessions to Stream"
                value={bookingData.session_to_stream || ""}
                onChange={(e) =>
                  handleChange("session_to_stream", e.target.value)
                }
              >
                <MenuItem value="all">All sessions</MenuItem>
                <MenuItem value="main">Main sessions only</MenuItem>
                <MenuItem value="specific">Specific sessions</MenuItem>
              </CustomSelect>

              {bookingData.session_to_stream === "specific" && (
                <CustomTextField
                  label="Specify sessions to stream"
                  value={bookingData.specific_sessions || ""}
                  onChange={(e) =>
                    handleChange("specific_sessions", e.target.value)
                  }
                  multiline
                  rows={2}
                  placeholder="List the specific sessions..."
                />
              )}
            </Box>

            <Divider />

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!bookingData.record_stream}
                  onChange={(e) =>
                    handleChange("record_stream", e.target.checked)
                  }
                />
              }
              label="Record the stream for later viewing"
            />

            {bookingData.record_stream && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!bookingData.provide_on_demand_access}
                      onChange={(e) =>
                        handleChange(
                          "provide_on_demand_access",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Provide on-demand access after event"
                />
                {bookingData.provide_on_demand_access && (
                  <CustomTextField
                    label="Retention Duration"
                    value={bookingData.retention_duration || ""}
                    onChange={(e) =>
                      handleChange("retention_duration", e.target.value)
                    }
                    placeholder="e.g., 30 days, 6 months, 1 year"
                    helperText="How long should recordings be available?"
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider />

      {/* Branding & Customization */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <BrandingWatermarkIcon sx={{ mr: 1, color: color400 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Branding & Customization
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={!!bookingData.require_custom_branding}
              onChange={(e) =>
                handleChange("require_custom_branding", e.target.checked)
              }
            />
          }
          label="Custom branding required"
        />

        {bookingData.require_custom_branding && (
          <Box sx={{ mt: 2 }}>
            <CustomTextField
              fullWidth
              label="Branding Requirements"
              value={bookingData.branding_requirements || ""}
              onChange={(e) =>
                handleChange("branding_requirements", e.target.value)
              }
              multiline
              rows={3}
              placeholder="Describe your branding needs (logos, colors, themes, etc.)"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ConferenceStreamingStep;
